import { Plugin, PluginKey } from "prosemirror-state";

export const columnNormalizationKey = new PluginKey("lumirColumnNormalization");

/**
 * columnList 불변식을 유지하는 정규화 플러그인.
 *
 * 문제: 코어 removeBlocks/일반 편집은 column/columnList의 최소 content 규칙을 강제하지
 * 않는다 → 컬럼의 마지막 블록을 지우면 **빈 column**(content "blockContainer+" 위반),
 * 컬럼을 통째로 지우면 **1단 columnList**(content "column column+" 위반)가 되어 문서가
 * 깨지고 직렬화(nodeToBlock)가 오류날 수 있다.
 *
 * 해결: appendTransaction에서 매 docChanged마다 columnList를 검사해,
 *  - 내용 있는 컬럼이 2개 미만이면 columnList를 **풀어서**(unwrap) 남은 컬럼들의
 *    blockContainer들을 부모 blockGroup에 직접 펼친다(blockContainer는 blockGroupChild라 유효).
 *  - 내용이 전혀 없으면 columnList를 제거한다.
 * 정규화 후에는 더 고칠 게 없어 self-terminating.
 */
export function columnNormalization(): Plugin {
  return new Plugin({
    key: columnNormalizationKey,
    appendTransaction(transactions, _oldState, newState) {
      if (!transactions.some((tr) => tr.docChanged)) {
        return null;
      }

      const lists: { pos: number; node: any }[] = [];
      newState.doc.descendants((node: any, pos: number) => {
        if (node.type.name === "columnList") {
          lists.push({ pos, node });
          return false; // columnList는 중첩되지 않음 → 하위 순회 불필요
        }
        return undefined;
      });
      if (lists.length === 0) {
        return null;
      }

      const tr = newState.tr;
      let modified = false;

      // 위치 큰 것부터 처리해 앞쪽 위치가 어긋나지 않게 한다.
      for (let i = lists.length - 1; i >= 0; i--) {
        const { pos, node } = lists[i];

        const nonEmptyColumns: any[] = [];
        node.forEach((col: any) => {
          if (col.type.name === "column" && col.childCount > 0) {
            nonEmptyColumns.push(col);
          }
        });

        if (nonEmptyColumns.length >= 2) {
          continue; // 유효한 다단 → 유지
        }

        const from = tr.mapping.map(pos);
        const to = tr.mapping.map(pos + node.nodeSize);

        // 남은 컬럼들의 blockContainer들을 모아 columnList를 대체(unwrap).
        const blocks: any[] = [];
        nonEmptyColumns.forEach((col: any) => {
          col.forEach((bc: any) => blocks.push(bc));
        });

        if (blocks.length > 0) {
          tr.replaceWith(from, to, blocks);
        } else {
          tr.delete(from, to);
        }
        modified = true;
      }

      return modified ? tr : null;
    },
  });
}
